import type { QueryKey } from '@tanstack/react-query';
import type {
  PaginationState,
  ColumnDef,
  ColumnSizingState,
  Table,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import type React from 'react';
import type {
  ColumnDataType,
  FilterOperator,
  FilterValue,
} from '@kirin/api-client/contracts/table';
import type { SortOrder } from '@kirin/api-client/contracts/helpers';
import type { PopoverContent } from '@/components/ui/popover';
import {
  ColumnDataTypes,
  FilterOperators,
  isValidOperatorForDataType,
  isValuelessOperator,
  getOperatorsForDataType,
  isValidNumericFilterValue,
  isValidNumericFilterRange,
  evaluateNumericFilterRange,
} from '@kirin/api-client/contracts/table';

// Re-export types for convenience
export type { ColumnDataType, FilterOperator, FilterValue };
// Re-export const objects and utility functions for convenience
export {
  ColumnDataTypes,
  FilterOperators,
  isValidOperatorForDataType,
  isValuelessOperator,
  getOperatorsForDataType,
  isValidNumericFilterValue,
  isValidNumericFilterRange,
  evaluateNumericFilterRange,
};

// DO NOT CHANGE. IT WILL AFFECT HYDRATION OF THE TABLE STATE
export const DATA_TABLE_PERSISTENCE_KEYS = {
  url: {
    search: 'search',
    sort: 'sort',
    order: 'order',
    expanded: 'exp',
  },
  localStorage: {
    preferences: 'ui.data-table-preferences',
  },
} as const;

export type TableRowData = Record<string, unknown>;

/**
 * Filter option types
 */
export const FilterOptionTypes = {
  STATIC: 'static',
  DYNAMIC: 'dynamic',
} as const;

export type FilterOptionType =
  (typeof FilterOptionTypes)[keyof typeof FilterOptionTypes];

/**
 * Filter operator definition with value and label.
 */
export interface FilterOperatorObject {
  value: FilterOperator;
  label: string;
}

/**
 * Filter option with label for simple text-based options.
 */
export interface FilterOptionWithLabel {
  value: string;
  label: string;
  metadata?: Record<string, unknown>;
  render?: (option: FilterOption, isSelected: boolean) => React.ReactNode;
}

/**
 * Filter option with custom render function and required metadata.
 */
export interface FilterOptionWithRender {
  value: string;
  label?: string;
  metadata: Record<string, unknown>;
  render: (option: FilterOption, isSelected: boolean) => React.ReactNode;
}

export interface ColumnStatePersistenceOptions {
  urlKey: string;
  /** Column data type — used so the URL decoder preserves positional empties for numeric ranges. */
  dataType?: ColumnDataType;
}

/**
 * Filter option with value, label and optional custom render function.
 * Two variants:
 * 1. FilterOptionWithLabel: Used for simple text-based options
 * 2. FilterOptionWithRender: Used for custom rendered options with required metadata
 */
export type FilterOption = FilterOptionWithLabel | FilterOptionWithRender;

/**
 * Tooltip options for filter columns.
 * Controls how selected filter values are displayed in the tooltip.
 *
 * Fallback order:
 * 1. enableCellRenderer - uses the column's render function (filterOptions.render)
 * 2. render - custom render function for tooltip content
 * 3. labelFormatter - simple string transformation
 */
export interface FilterTooltipOptions {
  /**
   * Simple string transformation for tooltip labels.
   * Takes the filter value and returns the formatted string.
   */
  labelFormatter?: (value: string) => string;
  /**
   * Custom render function for tooltip content.
   * Takes the filter option and renders a React node.
   */
  render?: (option: FilterOption) => React.ReactNode;
  /**
   * When true, uses the same render function defined at filterOptions.render
   * (the column's cell renderer) for tooltip content.
   */
  enableCellRenderer?: boolean;
}

/**
 * Filter popover container config. Extends the popover's own props (so the
 * column can configure positioning, collision, focus, etc. fully) and adds
 * convenience sizing applied to the popover's inline style. `children`,
 * `asChild`, and `style` are managed internally.
 */
export interface FilterDropdownOptions extends Omit<
  React.ComponentProps<typeof PopoverContent>,
  'children' | 'asChild' | 'style'
> {
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
}

/**
 * Static filter options - predefined array of options.
 */
export interface StaticFilterOptions {
  type: typeof FilterOptionTypes.STATIC;
  options: FilterOption[];
  dropdown?: FilterDropdownOptions;
  render?: (option: FilterOption, isSelected: boolean) => React.ReactNode;
  tooltip?: FilterTooltipOptions;
}

/**
 * Parameters for dynamic filter options query
 */
export interface FilterOptionsParams {
  search?: string;
  limit?: number;
  offset?: number;
  operator?: FilterOperator;
  /** Whether the query should be enabled (e.g., when dropdown is open) */
  enabled?: boolean;
}

export interface DynamicFilterOptionItem {
  value: string;
  label?: string;
  metadata?: Record<string, unknown>;
}

export type DynamicFilterOptionsResult =
  | string[]
  | Array<DynamicFilterOptionItem>;

export interface DynamicFilterOptionsPage {
  options: DynamicFilterOptionsResult;
  total?: number;
  nextOffset?: number | null;
  hasMore?: boolean;
}

export interface FilterOptionsPagination {
  mode: 'offset';
  limit: number;
}

/**
 * Dynamic filter options - fetched from server via React Query.
 *
 * The queryFn should return an array of string values OR objects with metadata.
 * - If returning strings: Use labelFormatter to convert values to labels
 * - If returning objects with metadata: Should include value, and optional label/metadata
 * The getQueryKey function generates the query key based on params.
 */
export interface DynamicFilterOptions {
  type: typeof FilterOptionTypes.DYNAMIC;
  queryFn: (
    params: FilterOptionsParams
  ) => Promise<DynamicFilterOptionsResult | DynamicFilterOptionsPage>;
  getQueryKey: (params: FilterOptionsParams) => QueryKey;
  queryConfig?: {
    staleTime?: number;
    cacheTime?: number;
  };
  pagination?: FilterOptionsPagination;
  dropdown?: FilterDropdownOptions;
  labelFormatter?: (value: string) => string;
  render?: (option: FilterOption, isSelected: boolean) => React.ReactNode;
  tooltip?: FilterTooltipOptions;
  /**
   * KIR-3769: when true, the dropdown's type-ahead is filtered CLIENT-SIDE
   * against each option's formatted label instead of being forwarded to the
   * server as a `search` param. Use when the backend option value is an opaque
   * key (e.g. the composite `<subPolicyKey>::<type>` Title key) whose raw form
   * does not match what the user types. The full option set is fetched once
   * (search is stripped from the query key + queryFn params, so keystrokes
   * reuse the same cached fetch) and narrowed locally by `label`. Incompatible
   * with server-side `pagination` (the full set must be present to filter).
   */
  clientSideSearch?: boolean;
  /**
   * KIR-5098: render one free-text input with a "Matches" list of these
   * options (typed text drives the server search) instead of the pick list.
   * The applied value is the text itself; the operator decides how it matches.
   */
  suggestInput?: { label: string; placeholder?: string };
  /**
   * Keep the options in the order the server returned them instead of sorting
   * them alphabetically by label. Use when the server orders them by meaning,
   * e.g. most to least severe.
   */
  preserveServerOrder?: boolean;
}

/**
 * Filter options can be either static (array) or dynamic (fetched).
 */
export type FilterOptions = StaticFilterOptions | DynamicFilterOptions;

export interface TableQueryState {
  pagination: PaginationState;
  filters: FilterValue[];
  sort: SortState | null;
  search: string;
  expandedRowIds: Set<string>;
}

export interface SortState {
  field: string;
  order: SortOrder;
}

export type TableQueryAction =
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'ADD_FILTER'; payload: FilterValue }
  | { type: 'UPDATE_FILTER'; payload: { index: number; filter: FilterValue } }
  | { type: 'REMOVE_FILTER'; payload: number }
  | { type: 'SET_FILTERS'; payload: FilterValue[] }
  | { type: 'SET_SORT'; payload: SortState }
  | { type: 'TOGGLE_SORT'; payload: { field: string; order?: SortOrder } }
  | { type: 'CLEAR_SORT' }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'SET_STATE'; payload: TableQueryState }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'TOGGLE_ROW_EXPANSION'; payload: string }
  | { type: 'COLLAPSE_ALL_ROWS' }
  | { type: 'SET_EXPANDED_ROW_IDS'; payload: Set<string> }
  | { type: 'RESET' };

export interface PaginationResultsMeta {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface ActiveFilterMeta {
  field: string;
  operator: string;
  values: unknown[];
}

export interface ResponseMeta {
  activeFilters: ActiveFilterMeta[];
  search?: {
    value: string;
    fields: string[];
  };
}

export const DATA_SOURCE_MODES = {
  SERVER: 'server',
} as const;

export type DataSourceMode =
  (typeof DATA_SOURCE_MODES)[keyof typeof DATA_SOURCE_MODES];

/**
 * Column-sizing strategy for the DataTable.
 * - `'fixed'`: `table-fixed` + authoritative `<colgroup>` widths.
 * - `'auto'`: `table-auto`; columns flex between their `minWidth`/`maxWidth`.
 */
export type TableLayout = 'fixed' | 'auto';

export interface DataTableProps<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
> {
  id: string;
  mode: DataSourceMode;
  initialState?: Partial<TableQueryState>;
  search?: DataTableSearchConfig;
  queryFn: (
    params: DataFetchParams,
    signal?: AbortSignal
  ) => Promise<DataFetchResponse<TRow, TMeta>>;
  getQueryKeysFromParams: (params: DataFetchParams) => QueryKey;
  queryConfig?: QueryBehaviorConfig;
  /**
   * Extra values (beyond filters+search) that affect the total row count.
   * Passed through to useDataTableQuery's fingerprint so toggling these
   * values invalidates the cached knownTotal.
   */
  countScope?: Record<string, unknown>;
  columns: ColumnConfig<TRow>[];
  features?: DataTableFeatures;
  rowExpansion?: RowExpansionOptions<TRow>;
  /** Callback fired when a row is clicked. Called in addition to expansion if both are enabled. */
  onRowClick?: (row: TRow) => void;
  /**
   * Optional per-row className resolver. Returned classes are appended to the
   * default row classes (so default styling is preserved) and can be used by
   * callers to mark rows as e.g. muted or pending. Defaults to a no-op when
   * unset, keeping existing tables byte-identical.
   */
  getRowClassName?: (row: TRow) => string | undefined;
  /**
   * Opt-in multi-row selection + bulk-action toolbar (KIR-3252). Omit for the
   * default no-selection table — every existing consumer stays byte-identical.
   */
  rowSelection?: RowSelectionOptions<TRow>;
  /**
   * Egress for response-level metadata. Fired with the response `meta` whenever it
   * changes, so a parent can lift table-specific extras (e.g. a heartbeat or a total)
   * out of the otherwise self-contained table. A handler may narrow its param to any
   * subset of `TMeta` — naming a field absent from `TMeta` fails to compile.
   *
   * Must be a stable reference (a state setter or `useCallback`-memoized fn). It runs
   * in an effect keyed on the callback, so a new inline function each render refires it.
   */
  onMeta?: (meta: TMeta) => void;
  /**
   * Extra toolbar controls owned by the caller (e.g. a top-level scope
   * segmented control), rendered on the toolbar's right, before "Clear
   * filters". Omit for the default toolbar.
   */
  toolbarEndSlot?: React.ReactNode;
  /**
   * Opt-in per-user column customization (KIR-4089): a "Columns" toolbar
   * button with a show/hide + reorder panel, and a right-edge "more columns"
   * sign when the table overflows. The caller owns persistence. Omit for the
   * default table — existing consumers stay byte-identical.
   */
  columnCustomization?: ColumnCustomizationOptions;
  /**
   * Called when "Clear filters" is clicked, after the table's own filters are
   * cleared — lets a caller reset state kept outside the table (e.g. the
   * `toolbarEndSlot` control) back to its default.
   */
  onClearFilters?: () => void;
  /**
   * URL params the caller owns that "Clear filters" must drop alongside the
   * table's own (KIR-5088).
   *
   * `onClearFilters` is not enough on its own when the caller's state lives in
   * the URL: the caller's delete and the table's own URL sync write the same
   * search params in one tick, and the sync — computed from a snapshot taken
   * before the delete — carries the old value forward. Naming the key here
   * folds it into that single write. Pass it ALONGSIDE `onClearFilters`, which
   * still owns resetting any non-URL copy of the same state.
   *
   * Keys are literal param names (e.g. `sai-org-tls-appr`), not column ids,
   * and are expected to sit outside the `<tableId>.` namespace that
   * `clearTableParams` already handles.
   */
  clearFiltersUrlKeys?: string[];
  /**
   * Opaque value for an out-of-band dimension that changes the result set
   * (e.g. a scope toggle). When it changes the table returns to the first
   * page. Omit when no such dimension exists.
   */
  pageResetKey?: string;
  /**
   * Filter values the table must not keep. A persisted URL can still name a
   * value the column no longer offers (Skills owns `type=skill` once that
   * tab is on). Each entry is removed from a matching filter; a filter left
   * with no values is dropped.
   */
  droppedFilterValues?: ReadonlyArray<{ field: string; value: string }>;
  paginationOptions?: PaginationConfig;
  noDataLabel?: string;
  errorBoundaryFallback?: React.ReactElement;
  errorBoundaryLabel?: string;
  className?: string;
  containerClassName?: string;
  /** Classes for the inner `<table>` (merged after defaults; win on conflict). */
  tableClassName?: string;
  /**
   * Column-sizing strategy (default `'fixed'`). `'fixed'` pins columns to their
   * widths via a `<colgroup>` (stable, content-agnostic). `'auto'` lets columns
   * flex between each column's `minWidth`/`maxWidth` floor and ceiling. See
   * {@link TableLayout}.
   */
  tableLayout?: TableLayout;
}

/**
 * A user's column layout, keyed by column id. Both lists empty = the table's
 * default layout. Ids the table doesn't render are ignored (and preserved on
 * save, so one layout can serve tables with different column sets).
 */
export interface ColumnLayout {
  order: string[];
  hidden: string[];
  /** Widths (px) the user dragged, by column id; absent = default width. */
  widths?: Record<string, number>;
}

export interface ColumnCustomizationOptions {
  layout: ColumnLayout;
  onLayoutChange: (layout: ColumnLayout) => void;
}

export interface DataTableFeatures {
  enablePagination?: boolean;
  enableFiltering?: boolean;
  enableRowExpansion?: boolean;
  enableSorting?: boolean;
  enableLocalPreferences?: boolean;
  enableUrlState?: boolean;
  showToolbar?: boolean;
  showCardWrapper?: boolean;
  /** Show shadow on the table container (independent of showCardWrapper) */
  showShadow?: boolean;
}

export interface DataTableSearchConfig {
  placeholder?: string;
  className?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Called with the debounced, trimmed search term whenever it changes. */
  onSearch?: (term: string) => void;
}

export interface RowExpansionOptions<TRow extends TableRowData> {
  /** Icon shown in the expand button. Defaults to a chevron right. */
  icon?: React.ReactNode;
  /**
   * Per-row control drawn beside the expand button, in the same cell (which
   * widens from 40px to 80px when set). It must stop click propagation itself
   * if it should not also toggle the row.
   */
  toggleAddon?: (row: TRow) => React.ReactNode;
  /** Content to render when the row is expanded. If not provided, no expansion content is shown. */
  render?: (row: TRow) => React.ReactNode;
  contentHeight?: number | string;
  contentClassName?: string;
  rowClassName?: string;
  keepMounted?: boolean;
  mountOnExpand?: boolean;
  showContainer?: boolean;
  /** When true, clicking anywhere on the row expands it (default: true) */
  expandOnRowClick?: boolean;
  /**
   * Function to extract the unique identifier for expansion state.
   * This ID is used for tracking expanded rows and URL persistence.
   * Defaults to `row.id` if not provided.
   */
  getRowId?: (row: TRow) => string;
  /**
   * When provided, gates both the chevron cell and click-to-expand per row.
   * Returns `false` to render no expand affordance and skip expansion on
   * click for that row (KIR-3940 D1). Defaults to `true` — existing tables
   * that don't set it are unaffected.
   */
  canExpand?: (row: TRow) => boolean;
  /** When true, newly expanded rows receive a highlight ring for `highlightDuration` ms. */
  highlightOnExpand?: boolean;
  /** Duration in ms to show the highlight ring after expansion. Defaults to 3000. */
  highlightDuration?: number;
  /**
   * When true, the table scrolls to the first expanded row that exists in the
   * current page data on mount. No-op if the expanded row ID is not in the
   * paginated results.
   */
  scrollToExpandedRow?: boolean;
}

/**
 * Context handed to a row-selection table's bulk-action toolbar (KIR-3252).
 * This is the engine's egress for selection + query state — `DataTableInner`
 * otherwise keeps that state fully internal. The consuming toolbar (e.g. the
 * alerts bulk bar) uses it to build the backend selection descriptor and the
 * count copy.
 */
export interface BulkToolbarContext<TRow extends TableRowData> {
  /** Selected row ids on the current page (keyed by `RowSelectionOptions.getRowId`). */
  selectedRowIds: string[];
  /** Resolved row objects for the current page's selected ids. */
  selectedRows: TRow[];
  /** All row ids on the current page. */
  pageRowIds: string[];
  /** Clear all selection (explicit keys AND select-all/exclusions). */
  clearSelection: () => void;
  /** True when the user chose "select all matching across pages". */
  selectAllAcrossPages: boolean;
  /**
   * Keys the user explicitly excluded while in select-all mode (the effective
   * selection is "all matching − excludedRowIds"). Empty in keys mode.
   */
  excludedRowIds: string[];
  /** Enter select-all-across-pages mode (discards any explicit key selection). */
  enterSelectAllAcrossPages: () => void;
  isAllPageSelected: boolean;
  isSomePageSelected: boolean;
  /** Active column filters driving the list (maps to the `query` selection). */
  filters: FilterValue[];
  /** Active search term (empty string when none). */
  search: string;
  /** Total matching rows across all pages. */
  total: number;
  /** Number of pages for the current filter/search. */
  pageCount: number;
  /** Current page size. */
  pageSize: number;
}

/**
 * Opt-in row-selection config for {@link DataTableProps}. When omitted (the
 * default) the table renders exactly as before — no checkbox column, no
 * selection state. When provided, a leading checkbox column is injected and a
 * bulk toolbar slot renders below the rows, sticky to the bottom of the view.
 */
export interface RowSelectionOptions<TRow extends TableRowData> {
  /** Stable unique id per row — selection is keyed by this (use the domain key). */
  getRowId: (row: TRow) => string;
  /**
   * Render the bulk-action toolbar below the rows. Receives the live
   * selection + query context. Return `null` to render nothing (e.g. when no
   * rows are selected). Must be a stable/memoized component for perf.
   */
  renderBulkToolbar?: (ctx: BulkToolbarContext<TRow>) => React.ReactNode;
  /**
   * Hang the bulk bar just below the table instead of laying it out after it,
   * for a table that fills a fixed-height box (the Alerts tables): selecting
   * then never shrinks the table. The bar takes no space, so the room under
   * that box must fit one bar line, and the box must not clip it.
   */
  hangBulkToolbar?: boolean;
  /**
   * Opaque token folded into the selection-reset key alongside filters/search.
   * Change it to clear the current selection on a result-set change the engine
   * can't see itself — e.g. an out-of-band visibility/tab toggle that swaps the
   * underlying list without touching filters or the search term. Omit when no
   * such external dimension exists.
   */
  resetToken?: string;
  /**
   * Accessible name for a row's checkbox, so screen-reader users can tell
   * which row it controls. Pass a stable (memoized) function. Defaults to
   * `Select alert <row id>` (the Alerts tables).
   */
  getRowAriaLabel?: (row: TRow) => string;
}

export interface QueryBehaviorConfig {
  staleTime?: number;
  gcTime?: number;
  retry?: number | boolean;
  retryDelay?: number | ((attemptIndex: number) => number);
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  refetchInterval?: number | false;
}

export interface DataFetchParams {
  pagination: PaginationState;
  filters: FilterValue[];
  sort?: SortState | null;
  search?: string;
  /** Previously fetched total — passed when only sort/page changed so the
   *  backend can skip the expensive COUNT query. */
  knownTotal?: number;
}

export interface DataFetchResponse<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
> {
  data: TRow[];
  pagination: PaginationResultsMeta;
  meta: TMeta;
}

/**
 * Base column configuration shared by all column types.
 */
interface BaseColumnConfig<TRow extends TableRowData> extends Omit<
  ColumnDef<TRow>,
  'id' | 'header' | 'cell'
> {
  id: string;
  header: string;
  accessor?: keyof TRow | ((row: TRow) => unknown);
  description?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  renderCell?: (context: CustomCellContext<TRow>) => React.ReactNode;
  range?: {
    min: number;
    max: number;
    step?: number;
  };
  sortable?: boolean;
  sortField?: string;
  sortKey?: string;
  /**
   * When true the column participates in filtering/URL-state/persistence but is
   * NOT rendered as a visible header or body cell (KIR-3769). Used to keep a
   * back-compat filter field alive (e.g. the legacy `type` filter behind the
   * ALERTS_REVAMP `title` column) so deep links that target its `urlKey` still
   * resolve to a valid filterable field instead of being silently dropped.
   */
  hidden?: boolean;
  /**
   * KIR-5537: a `hidden` column that shows, with its header filter, while it
   * has an active filter, so a filter set by a link never applies unseen.
   */
  showWhenFiltered?: boolean;
  /**
   * Optional child columns for grouped headers.
   * When present:
   * - Header row 1: Shows group header with colSpan
   * - Header row 2: Shows child headers
   * - TanStack receives only leaf columns for cell rendering
   */
  children?: ColumnConfig<TRow>[];
  /** Class name for the header cell when grouped headers exist */
  headerClassName?: string;
  /**
   * Optional adornment rendered right after the header label (e.g. a "NEW"
   * pill on a newly added column). Display only — not part of the label used
   * for filter names or the header's title attribute.
   */
  headerBadge?: React.ReactNode;
  /**
   * Whether body cells clip overflowing content (with an ellipsis for inline
   * text) so it can't paint over the next column (KIR-4877/4762/4587).
   * Defaults to true, except DATE columns, which default to false so a
   * timestamp is never cut. Set false on action-button columns. Applies to
   * leaf columns only; it is ignored on a group column with `children`.
   */
  clipOverflow?: boolean;
  /**
   * Marks a column that renders a timestamp. It is never clipped, and its
   * width follows the user's Timestamp Format (relative vs absolute) and
   * display zone via `getTimestampColumnWidth`, fixed in either layout (min =
   * max = width; a resizable table lets the user widen it, never narrow it).
   * Overrides `width` and `maxWidth`; `minWidth` raises it (e.g. for a header
   * wider than the value) but never lowers it.
   */
  timestamp?: boolean;
  /**
   * Fixed layout only: the column has no set width and takes the table's
   * remaining space, never less than `minWidth` (the table scrolls instead).
   * Use on one column per table; `table-fixed` splits space equally between
   * several fill columns and ignores their individual minimums.
   */
  fill?: boolean;
}

/**
 * Filterable column configuration.
 * Requires dataType and filterOperators to be specified.
 */
/**
 * KIR-5098: a second field one column's filter can drive, offered as an extra
 * entry in the same operator dropdown (e.g. an endpoint name filter whose
 * column also keeps its endpoint-count range). Choosing it swaps the input to
 * the alternate data type and applies to `field`; the two never apply at once.
 */
export interface AlternateColumnFilter {
  field: string;
  /** Operator-dropdown label, e.g. "Count range". */
  label: string;
  dataType: ColumnDataType;
  /** Operator sent for `field`, e.g. includes for a [min, max] range. */
  operator: FilterOperator;
  /** Persists the alternate filter (URL / saved state) under this key. */
  urlKey?: string;
}

export interface FilterableColumnConfig<
  TRow extends TableRowData,
> extends BaseColumnConfig<TRow> {
  filterable: true;
  dataType: ColumnDataType;
  filterOperators: FilterOperatorObject[];
  filterOptions?: FilterOptions;
  statePersistence?: ColumnStatePersistenceOptions;
  alternateFilter?: AlternateColumnFilter;
}

/**
 * Non-filterable column configuration.
 * Does not include filter-related properties.
 */
interface NonFilterableColumnConfig<
  TRow extends TableRowData,
> extends BaseColumnConfig<TRow> {
  filterable?: false;
}

/**
 * Extended column configuration that builds on TanStack Table's ColumnDef.
 * Provides a simplified accessor interface while maintaining full compatibility.
 *
 * Type is discriminated by `filterable`:
 * - If filterable=true, dataType and filterOperators are required
 * - If filterable=false/undefined, filter properties are not included
 */
export type ColumnConfig<TRow extends TableRowData> =
  | FilterableColumnConfig<TRow>
  | NonFilterableColumnConfig<TRow>;

/**
 * Checks if a column has sub-headers (for visual grouping).
 */
export function isGroupColumn<TRow extends TableRowData>(
  column: ColumnConfig<TRow>
): boolean {
  return Boolean(column.children && column.children.length > 0);
}

/**
 * Checks if a column is a leaf column (renders data).
 */
export function isLeafColumn<TRow extends TableRowData>(
  column: ColumnConfig<TRow>
): boolean {
  return !isGroupColumn(column);
}

/**
 * Checks if any columns have grouped headers.
 */
export function hasGroupColumns<TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[]
): boolean {
  return columns.some(isGroupColumn);
}

/**
 * Simplified cell context for custom cell renderers.
 * Provides commonly needed properties without the complexity of TanStack's CellContext.
 */
export interface CustomCellContext<TRow extends TableRowData> {
  value: unknown;
  row: TRow;
  column: ColumnConfig<TRow>;
  rowIndex: number;
}

export interface PaginationConfig {
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  showPageNumbers?: boolean;
  showNavigationButtons?: boolean;
  showResultsIndicator?: boolean;
}

export interface UseDataTableQueryOptions<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
> extends Pick<
  DataTableProps<TRow, TMeta>,
  'queryFn' | 'getQueryKeysFromParams' | 'queryConfig' | 'onMeta' | 'countScope'
> {
  pagination: TableQueryState['pagination'];
  filters: TableQueryState['filters'];
  sort?: TableQueryState['sort'];
  search?: TableQueryState['search'];
}

export interface UseDataTableInstanceOptions<TRow extends TableRowData> {
  columns: ColumnConfig<TRow>[];
  data: TRow[];
  paginationMeta: PaginationResultsMeta;
  pagination: PaginationState;
  /** When true, inject a leading checkbox column and enable (multi) selection. */
  enableRowSelection?: boolean;
  /** When true, the checkbox column inverts (rowSelection holds exclusions). */
  selectAllAcrossPages?: boolean;
  /** Stable row id — required when `enableRowSelection` is true. */
  getRowId?: (row: TRow) => string;
  /** Controlled selection state (owned by `DataTableInner`). */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /**
   * Invoked when the header checkbox is clicked while in select-all-across-pages
   * mode. The header is a GLOBAL toggle there (not per-page), so this clears the
   * whole cross-page selection. Omitted in keys mode (header toggles the page).
   */
  onSelectAllModeToggle?: () => void;
  /** Row checkbox accessible name — see `RowSelectionOptions.getRowAriaLabel`. */
  getRowAriaLabel?: (row: TRow) => string;
  /** Show a drag strip on each column's edge (fixed layout only). */
  enableColumnResizing?: boolean;
  /** Controlled column widths by id; omit to leave sizing uncontrolled. */
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>;
}

export interface UseDataTableStateOptions {
  initialState?: Partial<TableQueryState>;
}

export interface TableHeaderProps<TRow extends TableRowData> {
  table: Table<TRow>;
  columns: ColumnConfig<TRow>[];
  filters: FilterValue[];
  onFilterChange: (filter: FilterValue) => void;
  sort?: SortState | null;
  onSortChange?: (field: string, order?: SortState['order']) => void;
  enableSorting?: boolean;
  enableRowExpansion?: boolean;
  /** Column-sizing strategy; drives per-cell width vs min/max styles. */
  tableLayout?: TableLayout;
  /**
   * Lets the user drag a header onto another to move the column into its
   * slot. Called with the two column ids; absent = headers are not draggable.
   */
  onColumnMove?: (fromId: string, toId: string) => void;
}

export interface TableBodyProps<TRow extends TableRowData> {
  table: Table<TRow>;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  noDataLabel?: string;
  enableRowExpansion?: boolean;
  /** Column-sizing strategy; drives per-cell width vs min/max styles. */
  tableLayout?: TableLayout;
  rowExpansion?: RowExpansionOptions<TRow>;
  /** Callback fired when a row is clicked */
  onRowClick?: (row: TRow) => void;
  /** Current search value for empty state messaging */
  searchValue?: string;
  /** Number of active filters for empty state messaging */
  activeFilterCount?: number;
  /** Expanded row IDs from table state */
  expandedRowIds?: Set<string>;
  /** Callback to toggle a row's expansion state */
  onToggleRowExpansion?: (rowId: string) => void;
  /**
   * Optional per-row className resolver. See {@link DataTableProps.getRowClassName}.
   */
  getRowClassName?: (row: TRow) => string | undefined;
}

export interface TableFooterProps<TRow extends TableRowData> {
  pageSizeOptions: number[];
  table: Table<TRow>;
  paginationState: PaginationState;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export interface TableEmptyViewProps {
  noDataLabel?: string;
  columnCount: number;
  /** Current search value for dynamic messaging */
  searchValue?: string;
  /** Number of active filters for dynamic messaging */
  activeFilterCount?: number;
}

export interface TableErrorViewProps {
  error?: string;
  columnCount: number;
}

export interface TableLoadingViewProps {
  columnCount: number;
}
